import { Calendar, ChatLines, Mail, PageEdit, Phone, PhoneOutcome, VideoCamera } from 'iconoir-react'
import type { Contact } from 'app/generated/graphql'

export type CommType = `call` | `text` | `email` | `form` | `chat` | `scheduler` | `followUp`
export type CommDirection = `inbound` | `outbound`
export interface LastComm {
  type?: CommType
  direction?: CommDirection
  date: string
}

/** Stored channels and the activity names used by hover details. */
export const LAST_COMM_CHANNELS: ReadonlyMap<string, CommType> = new Map<string, CommType>(
  Object.entries({
    call: `call`,
    text: `text`,
    email: `email`,
    form: `form`,
    chat: `chat`,
    scheduler: `scheduler`,
    followUp: `followUp`,
    Call: `call`,
    Form: `form`,
    [`Imported Form`]: `form`,
    Chat: `chat`,
    [`Online Scheduler`]: `scheduler`,
    Scheduler: `scheduler`,
    [`Virtual Consultation`]: `scheduler`,
    FollowUp: `followUp`,
  } satisfies Record<string, CommType>),
)

/** The stored date and descriptor always describe the same communication, including archived texts. */
export function contactLastComm(
  contact: Pick<Contact, `lastCommDate` | `lastCommActivityType` | `lastCommActivitySubType`>,
): LastComm | undefined {
  if (!contact.lastCommDate) return undefined
  const type = LAST_COMM_CHANNELS.get(contact.lastCommActivityType ?? ``)
  const subType = contact.lastCommActivitySubType
  let direction: CommDirection | undefined
  if (type === `call` || type === `text` || type === `email`) {
    if (subType === `inbound` || subType === `outbound`) direction = subType
  } else if (type === `form` || type === `chat` || type === `scheduler`) {
    direction = `inbound`
  } else if (type === `followUp`) {
    direction = `outbound`
  }
  return { type, direction, date: String(contact.lastCommDate) }
}

export function lastCommLabel(comm?: LastComm): string {
  const typeLabel = lastCommTypeLabel(comm?.type) ?? `Last communication`
  if (comm?.direction === `inbound`) return `Inbound ${typeLabel}`
  if (comm?.direction === `outbound`) return `Outbound ${typeLabel}`
  return typeLabel
}

/** Compact label for the activity type, shown next to the date in the cell. */
export function lastCommTypeLabel(activityType?: string): string | undefined {
  if (!activityType) return undefined
  switch (activityType) {
    case `call`:
      return `Call`
    case `text`:
      return `Text`
    case `email`:
      return `Email`
    case `form`:
      return `Form`
    case `chat`:
      return `Chat`
    case `scheduler`:
      return `Scheduler`
    case `followUp`:
    case `FollowUp`:
      return `Follow Up`
    case `Imported Form`:
      return `Form`
    case `Online Scheduler`:
    case `Scheduler`:
      return `Scheduler`
    case `Virtual Consultation`:
      return `Consult`
    default:
      return activityType
  }
}

/**
 * Icon per communication medium; same icon vocabulary as the system tag chips.
 * Sits alongside comActivityTypes, the list it maps over, so the Last Comm cell
 * and its tooltip can share it without importing each other.
 */
export function lastCommIcon(activityType?: string) {
  switch (activityType) {
    case `call`:
    case `Call`:
      return Phone
    case `text`:
    case `chat`:
    case `Chat`:
      return ChatLines
    case `email`:
      return Mail
    case `form`:
    case `Form`:
    case `Imported Form`:
      return PageEdit
    case `scheduler`:
    case `Online Scheduler`:
    case `Scheduler`:
      return Calendar
    case `Virtual Consultation`:
      return VideoCamera
    case `followUp`:
    case `FollowUp`:
      return PhoneOutcome
    default:
      return undefined
  }
}
